import React, { useEffect, useMemo, useState } from 'react';
import styles from './styles.module.css';
import { Box, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import IconAdd from '@mui/icons-material/Add';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ModuleCard from '../moduleCard/ModuleCard';
import CustomButton from '../../../../components/buttons/CustomButton';
import AttachmentViewerPopup from '../../../../components/patient-attachments/AttachmentViewerPopup';
import PatientAttachmentUploadPopup from '../../../../popup/PatientAttachmentUploadPopup/PatientAttachmentUploadPopup';
import EvolutionPhotoEditPopup from '../../../../popup/EvolutionPhotoEditPopup/EvolutionPhotoEditPopup';
import { DataProviderInstance } from '../../../../api/DataProvider';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import EvolutionPhotosReportTemplate from '../../../../components/documents-templates/EvolutionPhotosReportTemplate';
import { buildEvolutionPhotosJob } from '../../modules/reportSections';
import EvolutionPhotoFilters from './EvolutionPhotoFilters';
import EvolutionPhotoTimeline from './EvolutionPhotoTimeline';
import EvolutionPhotoCompare from './EvolutionPhotoCompare';
import EvolutionPhotosExample from './EvolutionPhotosExample';
import {
  EVOLUTION_PHOTO_ANGLES,
  EVOLUTION_PHOTO_CONTEXT,
  EVOLUTION_PHOTO_REGIONS,
  anglesInPhotos,
  applyFilters,
  buildPhotoFields,
  filterEvolutionPhotos,
  groupPhotosBySession,
  placeInComparison,
  regionsInPhotos,
  resolveComparison,
  sortByDateAsc,
} from '../../modules/evolutionPhotos';

const MAX_NOTE_LENGTH = 255;

/**
 * Módulo "Fotos de evolução": linha do tempo das sessões (data + atendimento)
 * à esquerda e o comparador de duas fotos sempre visível à direita. Ao abrir,
 * a foto mais antiga e a mais recente do filtro já aparecem comparadas;
 * clicar numa miniatura da linha do tempo troca a foto do lado ativo.
 *
 * As fotos são anexos (context 'evolution'); `attachments` é a lista que a
 * página já carregou. Depois de enviar, editar ou excluir, o card recarrega só
 * a própria lista via list.php, sem recarregar a página.
 *
 * `enabled`: false na aba Saúde do paciente (linha do tempo e comparador, sem
 * envio nem edição, mas ainda exporta).
 * `patientName`, `professionalId`, `companyConfig`: identificação no PDF da
 * comparação.
 */
const EvolutionPhotos = ({
  attachments,
  patientId,
  appointmentId,
  currentDate,
  enabled,
  collapsible = false,
  onUpdated,
  patientName,
  professionalId,
  companyConfig,
}) => {
  const { ListPatientAttachments, GetPatientAttachmentFile } = DataProviderInstance();

  const [photos, setPhotos] = useState(filterEvolutionPhotos(attachments));
  const [filters, setFilters] = useState({ region: '', angle: '' });
  // Ids escolhidos para comparar ({ before, after }); nulo ou inválido cai no padrão
  const [selection, setSelection] = useState(null);
  // Lado que recebe o próximo clique na linha do tempo
  const [activeSide, setActiveSide] = useState('after');
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    setPhotos(filterEvolutionPhotos(attachments));
  }, [attachments]);

  function loadPhotos() {
    ListPatientAttachments(patientId, EVOLUTION_PHOTO_CONTEXT, (response) => {
      if (response && response.status === 'SUCCESS') {
        setPhotos(filterEvolutionPhotos(response.data || []));
      }
    });
    if (onUpdated) onUpdated();
  }

  const filtered = useMemo(() => applyFilters(photos, filters), [photos, filters]);
  const sessions = useMemo(() => groupPhotosBySession(filtered), [filtered]);
  const timelinePhotos = useMemo(() => sortByDateAsc(filtered), [filtered]);
  const pair = useMemo(() => resolveComparison(selection, timelinePhotos), [selection, timelinePhotos]);
  const hasFilters = regionsInPhotos(photos).length > 0 || anglesInPhotos(photos).length > 0;

  // PDF da comparação atual: baixa as duas fotos antes de montar o template
  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: 'fotos-de-evolucao',
    prepare: () => buildEvolutionPhotosJob(GetPatientAttachmentFile, pair),
  });

  function pickFromTimeline(photo) {
    const next = placeInComparison(pair, activeSide, photo);
    setSelection({
      before: next.before ? next.before.id : null,
      after: next.after ? next.after.id : null,
    });
  }

  function uploadHideHandler(success) {
    setShowUpload(false);
    if (success) {
      // A foto nova entra na comparação: volta ao padrão (mais antiga x mais recente)
      setSelection(null);
      loadPhotos();
    }
  }

  function editHideHandler(changed) {
    setEditing(null);
    if (changed) loadPhotos();
  }

  /** Campos por foto no popup de envio: região, ângulo e nota. */
  function renderItemFields(file, value, onChange) {
    const current = value || {};
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-start' }}>
        <FormControl size="small" sx={{ minWidth: 170, flex: 1 }}>
          <InputLabel>Região</InputLabel>
          <Select
            label="Região"
            value={current.region || ''}
            onChange={(event) => onChange({ ...current, region: event.target.value })}
          >
            <MenuItem value="">Sem região</MenuItem>
            {EVOLUTION_PHOTO_REGIONS.map((region) => (
              <MenuItem key={region.key} value={region.key}>{region.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
          <InputLabel>Ângulo</InputLabel>
          <Select
            label="Ângulo"
            value={current.angle || ''}
            onChange={(event) => onChange({ ...current, angle: event.target.value })}
          >
            <MenuItem value="">Sem ângulo</MenuItem>
            {EVOLUTION_PHOTO_ANGLES.map((angle) => (
              <MenuItem key={angle.value} value={angle.value}>{angle.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Nota"
          sx={{ minWidth: 180, flex: 2 }}
          value={current.note || ''}
          inputProps={{ maxLength: MAX_NOTE_LENGTH }}
          onChange={(event) => onChange({ ...current, note: event.target.value })}
        />
      </Box>
    );
  }

  const count = photos.length;
  const counterLabel = filtered.length === count
    ? `${count} ${count === 1 ? 'foto' : 'fotos'}`
    : `${filtered.length} de ${count} fotos`;

  return (
    <>
      <ModuleCard
        accent="purple"
        title="Fotos de evolução"
        icon={<PhotoLibraryOutlinedIcon />}
        description="Fotos por data, região e ângulo, com comparação"
        collapsible={collapsible}
        actions={(
          <>
            {pair.before && (
              <CustomButton
                variant="outlined"
                style="secondary"
                label={report.exporting ? 'Gerando...' : 'PDF'}
                icon={<PictureAsPdfOutlinedIcon />}
                enabled={!report.exporting}
                onClick={report.start}
              />
            )}
            {enabled && (
              <CustomButton
                variant="contained"
                style="primary"
                label="Adicionar fotos"
                icon={<IconAdd />}
                onClick={() => setShowUpload(true)}
              />
            )}
          </>
        )}
      >
        {count === 0 ? (
          <EvolutionPhotosExample enabled={enabled} />
        ) : (
          <div className={styles.content}>
            {hasFilters && (
              <div className={styles.toolbar}>
                <EvolutionPhotoFilters photos={photos} filters={filters} onChange={setFilters} />
                <span className={styles.counter}>{counterLabel}</span>
              </div>
            )}

            {filtered.length === 0 ? (
              <span className={styles.hint}>Nenhuma foto com esse filtro.</span>
            ) : (
              <div className={styles.layout}>
                <EvolutionPhotoTimeline
                  sessions={sessions}
                  appointmentId={appointmentId}
                  before={pair.before}
                  after={pair.after}
                  onPick={pickFromTimeline}
                />
                <EvolutionPhotoCompare
                  before={pair.before}
                  after={pair.after}
                  activeSide={activeSide}
                  onActiveSide={setActiveSide}
                  enabled={enabled}
                  onOpen={(photo) => setViewing(photo)}
                  onEdit={(photo) => setEditing(photo)}
                />
              </div>
            )}
          </div>
        )}
      </ModuleCard>

      {report.job && (
        <HiddenReport>
          <EvolutionPhotosReportTemplate ref={report.ref} {...report.paperProps} {...report.job} />
        </HiddenReport>
      )}

      {showUpload && (
        <PatientAttachmentUploadPopup
          title="Adicionar fotos de evolução"
          patientId={patientId}
          appointmentId={appointmentId}
          context={EVOLUTION_PHOTO_CONTEXT}
          defaultDate={currentDate}
          capture={true}
          accept="image/*"
          maxFiles={10}
          renderItemFields={renderItemFields}
          buildItemPayload={buildPhotoFields}
          OnHide={uploadHideHandler}
        />
      )}

      {editing && (
        <EvolutionPhotoEditPopup photo={editing} OnHide={editHideHandler} />
      )}

      {viewing && (
        <AttachmentViewerPopup
          attachment={viewing}
          list={timelinePhotos}
          enabled={false}
          OnHide={() => setViewing(null)}
        />
      )}
    </>
  );
};

export default EvolutionPhotos;
